import { calcularLinea, Precio, sumarLineas, useAuth, formatearMXN, type LineaCalculada } from '@eg/compartido'
import { Entrada } from '@eg/efectos'
import { ShoppingBag, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { es } from '../../content/es'
import { useCarrito } from '../../lib/carrito'
import { precioVigenteDe, useCatalogo, useListaSocio, type Presentacion, type ProductoCatalogo } from '../catalogo/datos'
import { NotaTipoCambio } from '../catalogo/NotaTipoCambio'
import { urlImagen } from '../catalogo/TarjetaProducto'
import { ControlCantidad } from './ControlCantidad'

const c = es.carrito

export type LineaDetallada = {
  presentacionId: string
  cantidad: number
  producto: ProductoCatalogo | null
  pres: Presentacion | null
  precio: { importe: number; incluyeIva: boolean; esSocio: boolean; usd?: number } | null
  calculo: LineaCalculada | null
}

/** Une las líneas del carrito con el catálogo y calcula el estimado de cada una (el servidor decide el real). */
export function useLineasDetalladas() {
  const { lineas } = useCarrito()
  const catalogo = useCatalogo()
  const socio = useListaSocio()
  const datos = catalogo.data
  const detalladas: LineaDetallada[] = lineas.map((l) => {
    const producto = datos?.productos.find((p) => p.presentaciones.some((x) => x.id === l.presentacionId)) ?? null
    const pres = producto?.presentaciones.find((x) => x.id === l.presentacionId) ?? null
    const precio = pres && datos ? precioVigenteDe(pres, datos.listaPublicaId, socio, datos.listas) : null
    const calculo = precio && producto ? calcularLinea(precio.importe, l.cantidad, Number(producto.iva_porcentaje), precio.incluyeIva) : null
    return { presentacionId: l.presentacionId, cantidad: l.cantidad, producto, pres: pres && pres.activa ? pres : null, precio, calculo }
  })
  const validas = detalladas.filter((l) => l.calculo)
  const totales = sumarLineas(validas.map((l) => l.calculo!))
  const hayProblemas = detalladas.some((l) => !l.calculo)
  return { detalladas, totales, hayProblemas, cargando: catalogo.isPending, error: catalogo.isError, tipoCambio: datos?.tipoCambio ?? null }
}

export function ResumenTotales({ totales }: { totales: LineaCalculada }) {
  return (
    <dl className="totales">
      <div>
        <dt>{c.subtotal}</dt>
        <dd>{formatearMXN(totales.base)} MXN</dd>
      </div>
      <div>
        <dt>{c.iva}</dt>
        <dd>{formatearMXN(totales.iva)} MXN</dd>
      </div>
      <div className="totales__total">
        <dt>{c.total}</dt>
        <dd>{formatearMXN(totales.total)} MXN</dd>
      </div>
    </dl>
  )
}

/** Una línea del carrito: imagen, nombre, precio estimado, cantidad y quitar. Aparte para verla en la guía visual. */
export function LineaVista({
  l,
  nombre,
  imagen,
  fijar,
  quitar,
}: {
  l: LineaDetallada
  nombre: string
  imagen: string | null
  fijar: (presentacionId: string, cantidad: number) => void
  quitar: (presentacionId: string) => void
}) {
  return (
    <article className="linea">
      <div className="linea__imagen">{imagen ? <img src={imagen} alt="" width={96} height={96} loading="lazy" /> : <span aria-hidden="true">·</span>}</div>
      <div className="linea__info">
        <h2 className="linea__nombre">{l.producto ? <Link to={`/producto/${l.producto.slug}`}>{nombre}</Link> : c.noDisponible}</h2>
        {l.pres && <p className="linea__detalle">{l.pres.contenido}</p>}
        {l.producto && l.pres && !l.calculo && <p className="campo__error">{c.sinPrecio}</p>}
        {!l.producto && <p className="campo__error">{c.noDisponible}</p>}
        {l.precio && (
          <p className="linea__precio">
            <Precio importe={l.precio.importe} usd={l.precio.usd} /> <span className="campo__ayuda">{l.precio.incluyeIva ? c.precioIvaIncluido : c.precioMasIva}</span>
            {l.precio.esSocio && <span className="linea__socio"> · {c.precioSocio}</span>}
          </p>
        )}
      </div>
      <div className="linea__acciones">
        {l.pres && (
          <ControlCantidad
            valor={l.cantidad}
            paso={l.pres.pedido_multiplo}
            minimo={l.pres.pedido_minimo}
            alCambiar={(n) => fijar(l.presentacionId, n)}
            etiqueta={`${c.cantidad}: ${nombre} ${l.pres.contenido}`}
          />
        )}
        {l.pres && l.pres.pedido_minimo > 1 && <p className="campo__ayuda">{c.minimo(l.pres.pedido_minimo, l.pres.pedido_multiplo)}</p>}
        <button type="button" className="boton boton--terciario boton--compacto" onClick={() => quitar(l.presentacionId)}>
          <Trash2 className="icono" strokeWidth={1.75} aria-hidden="true" />
          {c.quitar}
        </button>
      </div>
      {l.calculo && <p className="linea__total">{formatearMXN(l.calculo.total)} MXN</p>}
    </article>
  )
}

/** Carrito: líneas con imagen, cantidad y estimado, y el paso al pedido. */
export function Carrito() {
  const { lineas, fijar, quitar } = useCarrito()
  const { sesion } = useAuth()
  const { detalladas, totales, hayProblemas, cargando, error, tipoCambio } = useLineasDetalladas()

  return (
    <>
      <h1 className="titulo-seccion">{c.titulo}</h1>

      {lineas.length === 0 && (
        <div className="aviso-vacio">
          <ShoppingBag className="icono" strokeWidth={1.5} aria-hidden="true" />
          <h2>{c.vacioTitulo}</h2>
          <p>{c.vacioTexto}</p>
          <p className="accion">
            <Link className="boton boton--primario" to="/catalogo">
              {c.explorar}
            </Link>
          </p>
        </div>
      )}

      {lineas.length > 0 && cargando && (
        <div role="status" aria-live="polite">
          <p className="seccion__intro">{c.cargando}</p>
          <div className="esqueleto" />
        </div>
      )}
      {lineas.length > 0 && error && (
        <div className="aviso-vacio aviso-vacio--error" role="alert">
          <p>{es.catalogo.errorTexto}</p>
        </div>
      )}

      {lineas.length > 0 && !cargando && !error && (
        <div className="carrito">
          <ul className="carrito__lineas">
            {detalladas.map((l, i) => {
              const imagen = urlImagen(l.producto?.imagen_ruta ?? null)
              const nombre = l.producto?.nombre_comercial ?? '—'
              return (
                <li key={l.presentacionId}>
                  <Entrada tipo="fundir" retraso={Math.min(i, 4) * 0.05}>
                    <LineaVista l={l} nombre={nombre} imagen={imagen} fijar={fijar} quitar={quitar} />
                  </Entrada>
                </li>
              )
            })}
          </ul>

          <aside className="carrito__resumen" aria-label={es.checkout.resumen}>
            <ResumenTotales totales={totales} />
            <p className="campo__ayuda">{c.notaEstimado}</p>
            <NotaTipoCambio tipoCambio={tipoCambio} />
            {hayProblemas ? (
              <p className="campo__error" role="alert">
                {c.noDisponible}
              </p>
            ) : (
              <Link className="boton boton--primario boton--grande" to={sesion ? '/checkout' : '/ingresar'}>
                {c.continuar}
              </Link>
            )}
          </aside>
        </div>
      )}
    </>
  )
}
